"use client";
import { ChainIdNum, chainSettings, type ChainType } from "@/constants/chain-specific";
import { useEffect, useState } from "react";
import { useConnection, useSwitchChain } from "wagmi";
import { getChainIdBySlug } from "@/util/index";
import toast from '@/util/toast';

interface Props {
  chainId?: ChainType;
  chainSlug?: string;
}

export default function ChainSwitch({ chainId, chainSlug }: Props) {
  const { chainId: accountChainId, chain, isConnected } = useConnection();
  const [hasSwitched, setHasSwitched] = useState(false);
  const [isPageLoaded, setIsPageLoaded] = useState(false);

  const { switchChainAsync } = useSwitchChain();

  useEffect(() => {
    setIsPageLoaded(true);
    return () => setIsPageLoaded(false);
  }, []);

  useEffect(() => {
    if (!isPageLoaded || !isConnected || chainId === accountChainId || hasSwitched) return;
    if (chainId && chainId !== accountChainId) {
      const switchChainIfNeeded = async () => {
        try {
          await new Promise(resolve => setTimeout(resolve, 1500));

          const switchSuccess = await switchChainAsync({ chainId: chainId as ChainIdNum });
          if (switchSuccess) {
            toast.success(`Switched to ${chainSettings[chainId].chainName || chainId}`);
            setHasSwitched(true);
          }
        } catch (error) {
          if (error instanceof Error) {
            toast.error(`Failed to switch chain: ${error.message}`);
          } else {
            toast.error("Failed to switch chain: Unknown error");
          }
        }
      };
      switchChainIfNeeded();
    }
  }, [chainId, accountChainId, switchChainAsync, hasSwitched, isConnected, isPageLoaded]);


  useEffect(() => {
    if (!isPageLoaded || !isConnected || chainSlug && chainSlug.toLowerCase() === chain?.name.toLowerCase() || hasSwitched) return;
    if (chainSlug && chainSlug.toLowerCase() !== chain?.name.toLowerCase()) {
      const switchChainBySlugIfNeeded = async () => {
        await new Promise(resolve => setTimeout(resolve, 1000));

        const chainIdFromSlug = getChainIdBySlug(chainSlug);
        if (!chainIdFromSlug) {
          toast.error(`Failed to switch: Invalid chain slug "${chainSlug}"`);
          return;
        }
        try {
          await switchChainAsync({ chainId: chainIdFromSlug });
          toast.success(`Switched to ${chainSlug.charAt(0).toUpperCase() + chainSlug.slice(1)}`);
          setHasSwitched(true);
        } catch (error) {
          if (error instanceof Error) {
            toast.error(`Failed to switch chain: ${error.message}`);
          } else {
            toast.error("Failed to switch chain: Unknown error");
          }
        }
      };
      switchChainBySlugIfNeeded();
    }
  }, [chainSlug, chain?.name, switchChainAsync, hasSwitched, isConnected, isPageLoaded]);

  return <></>;
}
